import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Account, AccountManagementColumn, SortState } from '../types';
import { ClientSideRowModelModule, GridOptions, RowClickedEvent, themeQuartz, type ColDef, SortChangedEvent } from "ag-grid-community";
import { AgGridAngular } from 'ag-grid-angular';
import { AgTooltipCellComponent } from '../../../shared/components/ag-tooltip-cell/ag-tooltip-cell.component';
import { getManagementDateFormat } from '../../../utils/time';

@Component({
  selector: 'app-accounts-table',
  imports: [AgGridAngular],
  templateUrl: './accounts-table.component.html',
  styleUrl: './accounts-table.component.scss'
})
export class AccountsTableComponent {
  @Input() rows: Account[] = [];
  @Output() rowClicked = new EventEmitter<Account>();
  @Output() sortChanged = new EventEmitter<SortState>();

  colDefs: ColDef[] = this.getColDef();
  theme = themeQuartz.withParams({
    spacing: 10,
    headerBackgroundColor: 'rgba(217, 217, 217, 0.3)',
    rowHoverColor: 'rgba(233, 244, 245, 0.85)',
    fontSize: '14px',
    headerFontSize: '16px',
    fontFamily: 'Poppins',
    headerFontFamily: 'Poppins',
    headerFontWeight: 600,
    headerTextColor: '#717172;',
    cellTextColor: '#717172;',
    headerHeight: 64,
  });
  modules = [ClientSideRowModelModule];
  gridOptions: GridOptions = {
    suppressMovableColumns: true,
    domLayout: 'autoHeight',
    rowHeight: undefined,
    defaultColDef: {
      resizable: false,
      autoHeight: true,
      sortable: true,
      wrapText: true,
      flex: 1
    },
  };

  public onRowClicked = (event: RowClickedEvent): void => {
    this.rowClicked.emit(event.data as Account);
  };

  public onSortChanged(event: SortChangedEvent): void {
    // ag-grid fires sortChanged for non-user reasons too (initialization, row-data updates,
    // flex/resize). Only a real header click should reload the table.
    if (event.source !== 'uiColumnSorted') {
      return;
    }
    const sortModel = event.columns?.[0];
    this.sortChanged.emit({
      sortBy: sortModel?.getColId() || '',
      sortDir: (sortModel?.getSort() as 'asc' | 'desc') || 'desc',
    });
  }

  private getColDef(): ColDef[] {
    return [
      {
        field: AccountManagementColumn.NAME,
        flex: 3, // names are long — give this column the bulk of the width
      },
      {
        field: 'Users',
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data?.[AccountManagementColumn.USERS].length;
        },
        sortable: false,
        maxWidth: 110, // value is just a count
      },
      {
        field: AccountManagementColumn.BRANDS,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[AccountManagementColumn.BRANDS].length
        },
        sortable: false,
        maxWidth: 110, // value is just a count
      },
      {
        field: AccountManagementColumn.CREATED_AT,
        valueGetter: (params: any) => {
          return getManagementDateFormat(params.data[AccountManagementColumn.CREATED_AT])
        }
      },
    ];
  }
}
